import {
  GridSortModel,
  GridRowParams,
  GridValidRowModel,
  GridRowIdGetter,
  GridFilterModel,
  GridRowSelectionModel,
  GridCallbackDetails,
  DataGridProProps,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro';
import { ComponentType, ReactElement } from 'react';
import { GridSlotsComponentsProps } from '@mui/x-data-grid-pro';
import {
  DEFAULT_PAGINATION_CONFIG,
  DEFAULT_OPERATION_MODES,
  DEFAULT_SORT_MODEL,
} from '../constants';
import { DataGridInstanceName } from 'services/storage/keys';

/**
 * DataGrid configuration using Builder Pattern
 * 
 * Enables gradual building of configuration with default values.
 * All fields are optional (except storageKey), allowing flexible
 * configuration of only needed options.
 * 
 * @template T - Row type in DataGrid
 * 
 * @example
 * const config = createClientDataGridConfig<UserOnView>(DataGridInstanceName.user, INITIAL_SORT_MODEL)
 *   .withPagination({ defaultPageSize: 50, pageSizeOptions: [50, 100, 300] })
 *   .withRowDoubleClick((params) => goToUserDetails(params.row._id))
 *   .withNoRowsOverlay(SearchPhraseMismatchNoRowsOverlay)
 *   .build();
 */
export interface DataGridConfig<T extends GridValidRowModel = any> {
  dataGridInstanceName: DataGridInstanceName;
  defaultPageSize?: number;
  pageSizeOptions?: readonly number[];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  sortingMode?: 'client' | 'server';
  filterMode?: 'client' | 'server';
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  onRowDoubleClick?: (params: GridRowParams<T>) => void;
  noRowsOverlay?: () => ReactElement;
  slotProps?: GridSlotsComponentsProps;
  slots?: { toolbar?: ComponentType };
  columnGroupingModel?: any;
  getRowClassName?: (params: any) => string;
  getRowId?: GridRowIdGetter<T>;
  checkboxSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  disableMultipleRowSelection?: boolean;
  hideFooterSelectedRowCount?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel, details?: GridCallbackDetails<any>) => void;
  autosizeOptions?: DataGridProProps['autosizeOptions'];
  autosizeOnMount?: boolean;
  disablePagination?: boolean;
}

/**
 * Builder for DataGrid configuration
 * 
 * Implements Builder Pattern, enabling fluent API for building configuration.
 * All methods return `this`, allowing method chaining.
 * 
 * @template T - Row type in DataGrid
 * 
 * @example
 * const config = new DataGridConfigBuilder<UserOnView>()
 *   .withStorageKey('user-grid')
 *   .withClientSideOperations()
 *   .withPagination({ defaultPageSize: 50, pageSizeOptions: [50, 100, 300] })
 *   .withInitialSortModel([{ field: 'label', sort: 'asc' }])
 *   .build();
 */
export class DataGridConfigBuilder<T extends GridValidRowModel = any> {
  private config: Partial<DataGridConfig<T>> = {};

  withDataGridInstanceName(dataGridInstanceName: DataGridInstanceName): this {
    this.config.dataGridInstanceName = dataGridInstanceName;
    return this;
  }

  /**
   * Configure pagination settings
   * 
   * Both parameters are optional. If not provided, default values from
   * DEFAULT_PAGINATION_CONFIG will be used in build().
   * 
   * @param options - Optional pagination configuration
   * @param options.defaultPageSize - Default page size (defaults to DEFAULT_PAGINATION_CONFIG.defaultPageSize)
   * @param options.pageSizeOptions - Available page size options (defaults to DEFAULT_PAGINATION_CONFIG.pageSizeOptions)
   * 
   * @example
   * // Use all defaults
   * .withPagination()
   * 
   * // Override only page size
   * .withPagination({ defaultPageSize: 100 })
   * 
   * // Override both
   * .withPagination({ defaultPageSize: 50, pageSizeOptions: [50, 100, 300] })
   */
  withPagination(options?: {
    defaultPageSize?: number;
    pageSizeOptions?: readonly number[];
  }): this {
    if (options) {
      if (options.defaultPageSize !== undefined) {
        this.config.defaultPageSize = options.defaultPageSize;
      }
      if (options.pageSizeOptions !== undefined) {
        this.config.pageSizeOptions = options.pageSizeOptions;
      }
    }
    return this;
  }

  withInitialSortModel(sortModel: GridSortModel): this {
    this.config.initialSortModel = sortModel;
    return this;
  }

  withInitialFilterModel(filterModel: GridFilterModel): this {
    this.config.initialFilterModel = filterModel;
    return this;
  }

  withInitialColumnVisibilityModel(
    columnVisibilityModel: GridColumnVisibilityModel
  ): this {
    this.config.initialColumnVisibilityModel = columnVisibilityModel;
    return this;
  }

  withClientSideOperations(): this {
    this.config.sortingMode = 'client';
    this.config.filterMode = 'client';
    return this;
  }

  withServerSideOperations(): this {
    this.config.sortingMode = 'server';
    this.config.filterMode = 'server';
    return this;
  }

  withRowDoubleClick(handler: (params: GridRowParams<T>) => void): this {
    this.config.onRowDoubleClick = handler;
    return this;
  }

  withNoRowsOverlay(component: () => ReactElement): this {
    this.config.noRowsOverlay = component;
    return this;
  }

  withSlotProps(slotProps: GridSlotsComponentsProps): this {
    this.config.slotProps = slotProps;
    return this;
  }

  withSlots(slots: { toolbar?: ComponentType }): this {
    this.config.slots = slots;
    return this;
  }

  withColumnGrouping(model: any): this {
    this.config.columnGroupingModel = model;
    return this;
  }

  withRowSelection(
    options?: {
      checkboxSelection?: boolean;
      disableRowSelectionOnClick?: boolean;
      disableMultipleRowSelection?: boolean;
      hideFooterSelectedRowCount?: boolean;
    }
  ): this {
    if (options) {
      Object.assign(this.config, options);
    }
    return this;
  }

  withRowClassName(getRowClassName: (params: any) => string): this {
    this.config.getRowClassName = getRowClassName;
    return this;
  }

  withGetRowId(getRowId: GridRowIdGetter<T>): this {
    this.config.getRowId = getRowId;
    return this;
  }

  withControlledSorting(
    sortModel: GridSortModel,
    onSortModelChange: (model: GridSortModel) => void
  ): this {
    this.config.sortModel = sortModel;
    this.config.onSortModelChange = onSortModelChange;
    return this;
  }

  withControlledFiltering(
    filterModel: GridFilterModel,
    onFilterModelChange: (model: GridFilterModel) => void
  ): this {
    this.config.filterModel = filterModel;
    this.config.onFilterModelChange = onFilterModelChange;
    return this;
  }

  withControlledRowSelection(
    rowSelectionModel: GridRowSelectionModel | undefined,
    onRowSelectionModelChange: (model: GridRowSelectionModel, details?: GridCallbackDetails<any>) => void
  ): this {
    this.config.rowSelectionModel = rowSelectionModel;
    this.config.onRowSelectionModelChange = onRowSelectionModelChange;
    return this;
  }

  /**
   * Configure column autosizing behavior
   * 
   * Enables automatic column width adjustment based on content.
   * Autosizing is applied only to columns without an explicit `width`
   * (columns with `width` are excluded in the DataGrid component).
   * 
   * @param options - Optional autosizing configuration
   * @param options.includeHeaders - Include header text when calculating width (default: true)
   * @param options.includeOutliers - Include outlier cells (long content) when calculating width (default: false)
   * @param options.expand - Expand columns to fill available width if total is less (default: false)
   * 
   * @example
   * // Default: autosize all columns based on headers only
   * .withAutosizing()
   * 
   * // Enable expand to fill available space
   * .withAutosizing({ expand: true })
   * 
   * // Custom configuration
   * .withAutosizing({
   *   includeHeaders: true,
   *   includeOutliers: false,
   *   expand: true
   * })
   */
  withAutosizing(options?: {
    includeHeaders?: boolean;
    includeOutliers?: boolean;
    expand?: boolean;
  }): this {
    this.config.autosizeOptions = {
      includeHeaders: options?.includeHeaders ?? true,
      includeOutliers: options?.includeOutliers ?? false,
      expand: options?.expand ?? false,
      disableColumnVirtualization: true,
    };
    
    this.config.autosizeOnMount = true;
    
    return this;
  }

  withDisablePagination(): this {
    this.config.disablePagination = true;
    return this;
  }

  build(): DataGridConfig<T> {
    if (!this.config.dataGridInstanceName) {
      throw new Error('DataGrid instance name is required');
    }

    return {
      dataGridInstanceName: this.config.dataGridInstanceName,
      defaultPageSize: this.config.defaultPageSize ?? DEFAULT_PAGINATION_CONFIG.defaultPageSize,
      pageSizeOptions: this.config.pageSizeOptions ?? DEFAULT_PAGINATION_CONFIG.pageSizeOptions,
      initialSortModel: this.config.initialSortModel ?? DEFAULT_SORT_MODEL,
      sortingMode: this.config.sortingMode ?? DEFAULT_OPERATION_MODES.sortingMode,
      filterMode: this.config.filterMode ?? DEFAULT_OPERATION_MODES.filterMode,
      ...this.config,
    } as DataGridConfig<T>;
  }
}

/**
 * Helper function to create configuration for client-side DataGrid
 * 
 * Creates a new builder with preconfigured settings for client-side:
 * - Client-side sorting and filtering
 * - Provided storageKey
 * - Provided initialSortModel
 * 
 * @template T - Row type in DataGrid
 * @param storageKey - Key for state persistence in localStorage
 * @param initialSortModel - Initial sort model
 * @returns DataGridConfigBuilder ready for further configuration
 * 
 * @example
 * const config = createClientDataGridConfig<UserOnView>(
 *   'user_mui_data_grid_storage_key',
 *   [{ field: 'label', sort: 'asc' }]
 * )
 *   .withPagination({ defaultPageSize: 50, pageSizeOptions: [50, 100, 300] })
 *   .withRowDoubleClick((params) => goToUserDetails(params.row._id))
 *   .build();
 */
export function createClientDataGridConfig<T extends GridValidRowModel>(
  dataGridInstanceName: DataGridInstanceName,
  initialSortModel: GridSortModel,
  initialFilterModel?: GridFilterModel
): DataGridConfigBuilder<T> {
  const builder = new DataGridConfigBuilder<T>()
    .withDataGridInstanceName(dataGridInstanceName)
    .withClientSideOperations()
    .withInitialSortModel(initialSortModel);
  
  if (initialFilterModel) {
    builder.withInitialFilterModel(initialFilterModel);
  }
  
  return builder;
}

